// app/frontend/components/EventForm/EventForm.tsx
import { useState, useEffect, useMemo, useRef } from 'react';
import { useForm } from '@mantine/form';
import { Container, Paper, Title, Alert, Box } from '@mantine/core';
import { Notifications } from '@mantine/notifications';
import { IconAlertTriangle, IconInfoCircle, IconCheck } from '@tabler/icons-react';
import { useEditor } from '@tiptap/react';
import Highlight from '@tiptap/extension-highlight';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Heading from '@tiptap/extension-heading';
import Link from '@tiptap/extension-link';
import { useMediaQuery } from '@mantine/hooks';
import { useUser } from '../../src/context/UserContext';
import FacebookEventImporter from '../FacebookEventImporter';

// Import all sections
import EventInfoSection from './sections/EventInfoSection';
import LocationInfoSection from './sections/LocationInfoSection';
import DateTimeSection from './sections/DateTimeSection';
import EventDetailsSection from './sections/EventDetailsSection';
import ArtistSelectionSection from './sections/ArtistSelectionSection';
import LinksSection from './sections/LinksSection';
import EventFormButtons from './sections/EventFormButtons';

// Import custom hooks
import { useSlugValidation } from './hooks/useSlugValidation';
import { useTimezones } from './hooks/useTimezones';
import { useArtists } from './hooks/useArtists';
import { useEventSubmission } from './hooks/useEventSubmission';
import { useEventValidation } from './hooks/useEventValidation';

// Import utilities
import { filterUserOrganizers, sortByName } from './utils/formHelpers';
import { transformEditEventData, getCreateEventDefaults, isEditMode } from './utils/editEventTransform';

// Import types
import type { EventFormProps, EventFormValues, User, ImportedEventData } from './types';

const EventForm: React.FC<EventFormProps> = ({
  event,
  artists: propArtists,
  artist_pagination,
  event_types: propEventTypes,
  dance_styles: propDanceStyles,
  event_frequencies: propEventFrequencies,
  organizers: propOrganizers,
  errors,
  areas: propAreas,
  cities: propCities,
  timezones,
}) => {
  // -------------------------------
  // State & Context
  // -------------------------------
  const [isRecurring, setIsRecurring] = useState(false);
  const [seoImagePreview, setSeoImagePreview] = useState<string | null>(event.event_image_url);
  const [croppedImage, setCroppedImage] = useState<File | null>(null);

  const { user } = useUser() as { user: User | null };
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isDraft = event.event_status_id === 3;
  const validationRulesSet = useRef(false);

  // Determine initial values based on create vs edit mode
  const initialFormValues = useMemo(() => {
    if (isEditMode(event)) {
      // console.log('🔄 Edit mode detected, transforming event data:', event);
      return transformEditEventData(event);
    } else {
      // console.log('✨ Create mode detected, using default values');
      return getCreateEventDefaults();
    }
  }, [event]);

  // Extract initial values for state
  const [address, setAddress] = useState(initialFormValues.address);
  const [timezone, setTimezone] = useState(initialFormValues.timezone);

  // Create a simple validation first, then update it
  const [currentValidationRules, setCurrentValidationRules] = useState<any>({});

  // -------------------------------
  // Form Setup (create first, before other hooks)
  // -------------------------------
  const form = useForm<EventFormValues>({
    initialValues: initialFormValues,
    validate: currentValidationRules,
    validateInputOnChange: true,
  });

  // -------------------------------
  // Custom Hooks (now form exists)
  // -------------------------------
  const { slug, setSlug, slugStatus } = useSlugValidation(initialFormValues.slug, form.values.name);

  // Rails timezone data is already in the correct {value, label} format
  const formattedTimezones = timezones || undefined;

  const { timezoneOptions, isLoadingTimezones } = useTimezones(formattedTimezones);

  // DEBUG: Check what useTimezones is actually returning
  console.log('🔍 formattedTimezones input:', formattedTimezones?.slice(0, 3));
  console.log('🔍 original timezones from Rails:', timezones?.slice(0, 3));

  const { mergedArtists, isLoadingArtists, artistOptionsLoaded, fetchAllArtists, loadMoreArtists, hasMoreArtists } =
    useArtists(propArtists, artist_pagination);

  console.log('🎨 Artist Loading State:', {
    propArtistsCount: propArtists?.length,
    mergedArtistsCount: mergedArtists?.length,
    artistOptionsLoaded,
    hasMoreArtists,
    artistPagination: artist_pagination,
    formArtistIds: form.values.artistIds,
    firstFewMergedArtists: mergedArtists?.slice(0, 5)?.map((a) => ({ id: a.id, name: a.name })),
    missingFromMerged: form.values.artistIds?.filter((id) => !mergedArtists.find((a) => a.id === id)),
  });

  // console.log('🔍 Initial form values:', {
  //   slug: initialFormValues.slug,
  //   name: initialFormValues.name,
  //   id: initialFormValues.id,
  //   recurring_event_id: initialFormValues.recurring_event_id,
  // });

  // console.log('Slug field values:', {
  //   formSlug: form.values.slug,
  //   slugState: slug,
  //   initialSlug: initialFormValues?.slug,
  // });

  // // And log what the event object contains:
  // console.log('🔍 Event object from Rails:', {
  //   slug: event.slug,
  //   name: event.name,
  //   id: event.id,
  //   recurring_event: event.recurring_event,
  // });

  // -------------------------------
  // Rich Text Editor
  // -------------------------------
  const editor = useEditor({
    extensions: [
      StarterKit,
      Highlight,
      Underline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      Heading.configure({ levels: [1, 2, 3] }),
      Link.configure({ openOnClick: true }),
    ],
    content: form.values.description,
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      form.setFieldValue('description', html);
    },
  });

  // -------------------------------
  // Custom Validation (now editor is available)
  // -------------------------------
  const { validationRules } = useEventValidation({ isDraft, editor, event });

  // Set validation rules after editor and validation rules are ready
  // useEffect(() => {
  //   if (editor && !validationRulesSet.current) {
  //     console.log('✅ Setting up validation rules');
  //     setCurrentValidationRules(validationRules);
  //     validationRulesSet.current = true;
  //   }
  // }, [editor, validationRules]); // Fixed: removed 'form' from dependencies

  // -------------------------------
  // Event Submission Hook
  // -------------------------------
  const { submitEvent, isLoading } = useEventSubmission({
    form,
    event,
    editor,
    user,
    croppedImage,
  });

  // -------------------------------
  // Derived Data
  // -------------------------------
  const filteredOrganizers = useMemo(() => {
    const filtered = filterUserOrganizers(propOrganizers || [], user);
    return sortByName(filtered);
  }, [propOrganizers, user]);

  // -------------------------------
  // Utility Functions
  // -------------------------------
  function validateURL(input: string) {
    const urlPattern = /^(http:\/\/|https:\/\/)/i;
    return urlPattern.test(input);
  }

  // -------------------------------
  // Event Handlers
  // -------------------------------
  const handleImageChange = (file: File) => {
    form.setFieldValue('event_image', file);
    setSeoImagePreview(URL.createObjectURL(file));
  };

  const handleFacebookImport = async (importedData: ImportedEventData) => {
    // Update form values with imported data
    if (importedData.name) {
      form.setFieldValue('name', importedData.name);
    }

    if (importedData.description) {
      form.setFieldValue('description', importedData.description);
      if (editor) {
        editor.commands.setContent(importedData.description);
      }
    }

    // Handle address and timezone import with priority for geocoded timezone
    let geocodingSucceeded = false;

    if (importedData.address) {
      form.setFieldValue('address', importedData.address);
      setAddress(importedData.address);

      // Trigger geocoding to detect timezone and location data
      try {
        await geocodeImportedAddress(importedData.address);
        geocodingSucceeded = true;
        console.log('🛐 Geocoding succeeded, using geocoded timezone');
      } catch (error) {
        console.warn('🛐 Failed to geocode imported address:', error);
        // Continue with the import even if geocoding fails
      }
    }

    // Only use imported timezone if geocoding failed or no address was provided
    if (!geocodingSucceeded && importedData.timezone) {
      console.log('🛐 Using imported timezone as fallback');

      // Map Facebook's timezone to Rails timezone format
      let railsTimezone = importedData.timezone;

      if (importedData.timezone === 'UTC') {
        // Check if Rails has a timezone with value "UTC"
        const utcTimezone = timezones?.find((tz) => tz.value === 'UTC');
        if (utcTimezone) {
          railsTimezone = utcTimezone.value;
          console.log('🕐 Mapped Facebook UTC to Rails UTC timezone');
        } else {
          console.warn('🕐 No UTC timezone found in Rails data, using original value');
        }
      }

      form.setFieldValue('timezone', railsTimezone);
      setTimezone(railsTimezone);
    } else if (geocodingSucceeded) {
      console.log('🛐 Skipping imported timezone - using geocoded timezone instead');
    }

    // Handle dates and times
    if (importedData.event_start_date) {
      form.setFieldValue('event_start_date', importedData.event_start_date);
    }
    if (importedData.event_start_time) {
      form.setFieldValue('event_start_time', importedData.event_start_time);
    }
    if (importedData.event_end_date) {
      form.setFieldValue('event_end_date', importedData.event_end_date);
    }
    if (importedData.event_end_time) {
      form.setFieldValue('event_end_time', importedData.event_end_time);
    }

    // Handle detected data
    if (importedData.detected_artist_ids && importedData.detected_artist_ids.length > 0) {
      console.log('🎨 Importing artists:', {
        importedIds: importedData.detected_artist_ids,
        importedNames: importedData.detected_artist_names,
        artistOptionsLoaded,
        mergedArtistsCount: mergedArtists.length,
      });

      // Ensure all artists are loaded before setting form values
      if (!artistOptionsLoaded) {
        console.log('🎨 Loading all artists before setting imported IDs...');
        await fetchAllArtists();
      }

      // Verify the imported artists exist in our data
      const missingArtists = importedData.detected_artist_ids.filter(
        (id) => !mergedArtists.find((artist) => artist.id === id),
      );

      if (missingArtists.length > 0) {
        console.warn('🎨 Some imported artists not found in database:', {
          missingIds: missingArtists,
          availableIds: mergedArtists.map((a) => a.id).slice(0, 10),
        });
      }

      form.setFieldValue('artistIds', importedData.detected_artist_ids);
    }

    if (importedData.detected_dance_style_ids && importedData.detected_dance_style_ids.length > 0) {
      form.setFieldValue('dance_style_ids', importedData.detected_dance_style_ids);
    }

    if (importedData.detected_event_type_ids && importedData.detected_event_type_ids.length > 0) {
      form.setFieldValue('event_type_ids', importedData.detected_event_type_ids);
    }

    if (importedData.facebook_url) {
      form.setFieldValue('facebook_url', importedData.facebook_url);
    }

    if (importedData.spans_next_day !== undefined) {
      form.setFieldValue('spans_next_day', importedData.spans_next_day ? 'true' : 'false');
    }
  };

  // Helper function to geocode imported address and trigger timezone detection
  const geocodeImportedAddress = async (address: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
        console.warn('Google Maps Geocoder not available');
        reject(new Error('Google Maps not loaded'));
        return;
      }

      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode({ address }, async (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const place = results[0];
          console.log('🛐 Geocoded imported address:', place.formatted_address);

          // Extract location data similar to AddressAutocomplete
          const components = place.address_components || [];
          let cityName = '';
          let countryName = '';
          let countryCode = '';
          let stateName = '';

          components.forEach((component) => {
            const types = component.types;

            if (types.includes('locality')) {
              cityName = component.long_name;
            } else if (types.includes('administrative_area_level_1') && !cityName) {
              cityName = component.long_name;
            }

            if (types.includes('administrative_area_level_1')) {
              stateName = component.long_name;
            }

            if (types.includes('country')) {
              countryName = component.long_name;
              countryCode = component.short_name;
            }
          });

          // Find area from cities data (similar to AddressAutocomplete logic)
          let areaName = stateName || 'Area not found';
          let cityFound = false;

          if (propCities && cityName) {
            const foundCity = propCities.find((city) => city.name.toLowerCase() === cityName.toLowerCase());
            if (foundCity) {
              areaName = foundCity.area_name;
              cityFound = true;
            }
          }

          // Set city data if available
          if (cityName && countryCode) {
            const continentMap: { [key: string]: string } = {
              US: 'North America',
              CA: 'North America',
              MX: 'North America',
              GB: 'Europe',
              FR: 'Europe',
              DE: 'Europe',
              IT: 'Europe',
              ES: 'Europe',
              AU: 'Oceania',
              NZ: 'Oceania',
              JP: 'Asia',
              CN: 'Asia',
              IN: 'Asia',
              BR: 'South America',
              AR: 'South America',
              ZA: 'Africa',
              NG: 'Africa',
              EG: 'Africa',
            };

            const continentName = continentMap[countryCode] || 'Unknown';

            form.setFieldValue('city_data', {
              cityName,
              countryName,
              countryCode,
              stateName,
              continentName,
              cityFound,
              missingCityName: !cityFound ? cityName : null,
            });

            form.setFieldValue('missing_city_detected', !cityFound);
            form.setFieldValue('missing_city_name', !cityFound ? cityName : null);
          }

          // Trigger timezone detection using coordinates
          const location = place.geometry?.location;
          if (location) {
            const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
            const lng = typeof location.lng === 'function' ? location.lng() : location.lng;

            // Ensure lat and lng are numbers, not functions
            const latValue = typeof lat === 'function' ? lat() : lat;
            const lngValue = typeof lng === 'function' ? lng() : lng;

            try {
              await fetchTimezoneFromCoordinates(latValue, lngValue);
              resolve();
            } catch (timezoneError) {
              console.warn('Failed to fetch timezone for coordinates:', timezoneError);
              resolve(); // Don't fail the whole process if timezone fails
            }
          } else {
            resolve();
          }
        } else {
          console.warn('Geocoding failed:', status);
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  };

  // Helper function to fetch timezone from coordinates (replicates AddressAutocomplete logic)
  const fetchTimezoneFromCoordinates = async (lat: number, lng: number): Promise<void> => {
    console.log('🛐 Fetching timezone for imported address coordinates:', lat, lng);

    try {
      const apiKey = 'N2IE9BKU2EQR';
      const url = `https://api.timezonedb.com/v2.1/get-time-zone?key=${apiKey}&format=json&by=position&lat=${lat}&lng=${lng}`;

      const response = await fetch(url);
      const data = await response.json();

      console.log('🛐 TimeZoneDB response for imported address:', data);

      if (data.status === 'OK' && data.zoneName) {
        // Convert IANA to Rails format using the same logic as AddressAutocomplete
        const { convertIanaToRails, isValidRailsTimezone } = await import('./utils/timezoneHelpers');
        const railsTimezone = convertIanaToRails(data.zoneName);

        console.log('🛐 Timezone conversion for imported address:', {
          iana: data.zoneName,
          rails: railsTimezone,
          foundInRailsData: railsTimezone ? isValidRailsTimezone(timezones || [], railsTimezone) : false,
        });

        if (railsTimezone && isValidRailsTimezone(timezones || [], railsTimezone)) {
          form.setFieldValue('timezone', railsTimezone);
          setTimezone(railsTimezone);
          console.log('🛐 Successfully set timezone from imported address:', railsTimezone);
        } else {
          console.warn('🛐 No Rails timezone mapping found for imported address IANA timezone:', data.zoneName);
        }
      } else {
        console.error('🛐 Error in TimeZoneDB response for imported address:', data.message || 'Unknown error');
      }
    } catch (error) {
      console.error('🛐 Error fetching timezone for imported address:', error);
      throw error;
    }
  };

  // -------------------------------
  // Effects (with fixed dependencies)
  // -------------------------------
  useEffect(() => {
    form.setFieldValue('slug', slug);
  }, [slug]); // Fixed: removed 'form' from dependencies

  useEffect(() => {
    setIsRecurring(form.values.is_recurring === 'true');
  }, [form.values.is_recurring]); // Fixed: depend on specific form value, not whole form

  // Status mapping
  const statusMapping: Record<number, { status: string; color: string; icon: JSX.Element }> = {
    1: { status: 'Active', color: 'green', icon: <IconCheck size={16} /> },
    2: { status: 'Pending', color: 'yellow', icon: <IconAlertTriangle size={16} /> },
    3: { status: 'Draft', color: 'blue', icon: <IconInfoCircle size={16} /> },
  };

  const eventStatus =
    event.event_status_id && statusMapping[event.event_status_id]
      ? statusMapping[event.event_status_id]
      : { status: 'None', color: 'gray', icon: <IconInfoCircle size={16} /> };

  // -------------------------------
  // Render
  // -------------------------------
  return (
    <>
      <Notifications position="top-right" />
      <Container size="sm" style={{ minHeight: 'auto' }} px={isMobile ? '0px' : '8px'}>
        <Paper
          p={isMobile ? '8px' : '8px'}
          shadow="xs"
          mb="3rem"
          mx={16}
          my={64}
          style={{ borderRadius: '8px', backgroundColor: '#3d3c3e' }}
        >
          {/* Event Status Alert */}
          {event.event_status_id && (
            <Alert
              icon={eventStatus.icon}
              title={`Event Status: ${eventStatus.status}`}
              color={eventStatus.color}
              variant="light"
              mb="md"
            />
          )}

          {/* Title */}
          <Title order={3} c="#E4E4E7" mx="4px">
            {isEditMode(event) ? 'Edit Event' : 'Create New Event'}
          </Title>

          {/* Facebook Event Importer - Only show for create mode */}
          {!isEditMode(event) && <FacebookEventImporter onEventImported={handleFacebookImport} disabled={isLoading} />}

          {/* Form */}
          <form>
            {/* Event Info Section */}
            <EventInfoSection form={form} slug={slug} setSlug={setSlug} organizers={filteredOrganizers} user={user} />

            {/* Location Info Section */}
            <LocationInfoSection
              form={form}
              address={address}
              setAddress={setAddress}
              timezone={timezone}
              setTimezone={setTimezone}
              areas={propAreas || []}
              cities={propCities || []}
              railsTimezoneData={timezoneOptions}
            />

            {/* Date & Time Section */}
            <DateTimeSection
              form={form}
              isDraft={isDraft}
              isRecurring={isRecurring}
              setIsRecurring={setIsRecurring}
              eventFrequencies={propEventFrequencies || []}
            />

            {/* Event Details Section */}
            <EventDetailsSection
              form={form}
              isDraft={isDraft}
              editor={editor}
              eventTypes={propEventTypes}
              danceStyles={propDanceStyles}
              eventFrequencies={propEventFrequencies}
              seoImagePreview={seoImagePreview}
              onImageChange={handleImageChange}
            />

            {/* Artist Selection Section */}
            <ArtistSelectionSection
              form={form}
              artists={mergedArtists}
              isLoadingArtists={isLoadingArtists}
              artistOptionsLoaded={artistOptionsLoaded}
              fetchAllArtists={fetchAllArtists}
              loadMoreArtists={loadMoreArtists}
              hasMoreArtists={hasMoreArtists}
            />

            {/* Links Section */}
            <LinksSection form={form} />

            {/* Error Display */}
            {errors && Object.keys(errors).length > 0 && (
              <Box color="red" mt="md">
                {Object.entries(errors).map(([field, messages]) => (
                  <div key={field}>
                    {field}: {messages.join(', ')}
                  </div>
                ))}
              </Box>
            )}

            {/* Form Buttons */}
            <EventFormButtons form={form} isLoading={isLoading} onSubmit={submitEvent} event={event} />
          </form>
        </Paper>
      </Container>
    </>
  );
};

export default EventForm;
